'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import classnames from 'classnames';
import { nanoid } from 'nanoid';
import { Label, TextInput, Icon, FieldNote, TextList, LoadingIndicator, Button, } from '../../index.js';
export const FileUploadField = (_a) => {
    var { acceptedFileTypes, ariaDescribedBy, className, disabled = false, dragAndDropText, id, files = [], label = 'Attach file', name, placeholder, readOnly, onChange, hideLabel, inputMode, multiple, required, labelAfter, fieldNote = 'Attach up to 3 files (2MB max per file). Accepted file types: .doc, .docx, .jpeg, .png, .xlsx, .xls, .pdf, .txt, and .csv.', isError, optionalLabel, requiredLabel, maxFiles, maxFileSize, maxFileSizeErrorText = 'File size should not exceed 2 MB. Please choose another file.', removeFileButtonText = 'Remove file', statusLabel, variant, iconLabel } = _a, other = __rest(_a, ["acceptedFileTypes", "ariaDescribedBy", "className", "disabled", "dragAndDropText", "id", "files", "label", "name", "placeholder", "readOnly", "onChange", "hideLabel", "inputMode", "multiple", "required", "labelAfter", "fieldNote", "isError", "optionalLabel", "requiredLabel", "maxFiles", "maxFileSize", "maxFileSizeErrorText", "removeFileButtonText", "statusLabel", "variant", "iconLabel"]);
    const [filesState, setFilesState] = React.useState(files);
    const [isErrorState, setIsErrorState] = React.useState(isError);
    const [isDragging, setIsDragging] = React.useState(false);
    const [fieldNoteState, setFieldNoteState] = React.useState(fieldNote);
    const [idVar, setId] = React.useState('');
    const [ariaDescribedByVar, setAriaDescribedBy] = React.useState('');
    React.useEffect(() => {
        setId(id || nanoid());
        if (fieldNote) {
            setAriaDescribedBy(ariaDescribedBy || nanoid());
        }
    }, [ariaDescribedBy, fieldNote, id]);
    function formatBytes(bytes, decimals) {
        if (bytes === 0)
            return '0 Bytes';
        const k = 1024;
        const dm = decimals <= 0 ? 0 : decimals || 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    }
    function onDragOver() {
        if (!isDragging) {
            setIsDragging(true);
        }
    }
    function onDragEnd() {
        if (isDragging) {
            setIsDragging(false);
        }
    }
    function onFileRemove(id) {
        const files = filesState;
        const filesToUpdate = files.filter((file) => file.id !== id);
        if (isErrorState === true) {
            setIsErrorState(false);
        }
        setFilesState(filesToUpdate);
        if (onChange) {
            onChange();
        }
    }
    function onFileInputChange(e) {
        const fileObjects = e.target.files;
        if (!fileObjects)
            return;
        let files = [...filesState];
        Array.from(fileObjects).forEach((file) => {
            if (maxFileSize && file.size >= maxFileSize) {
                setFieldNoteState(maxFileSizeErrorText);
                setIsErrorState(true);
            }
            else {
                files.push({
                    id: nanoid(),
                    fileObject: file,
                });
                setIsErrorState(isError);
            }
        });
        if (maxFiles && files.length >= maxFiles) {
            files = files.splice(0, maxFiles);
        }
        setFilesState(files);
        if (onChange) {
            onChange();
        }
    }
    function renderFileListIconOrRemoveButton(file) {
        switch (statusLabel) {
            case 'success':
                return (_jsx(Icon, { name: "check", className: "sparky-c-file-upload-field__icon--success" }));
            case 'uploading':
                return (_jsx(LoadingIndicator, { className: "sparky-c-file-upload-field__loading-indicator" }));
            default:
                return (_jsx(Button, { hideText: true, iconName: "x", iconPosition: "before", text: removeFileButtonText, variant: "bare", onClick: () => {
                        onFileRemove(file.id);
                    } }));
        }
    }
    const isDisabled = disabled || (filesState && maxFiles && filesState.length >= maxFiles);
    const componentClassName = classnames('sparky-c-file-upload-field', className, {
        'sparky-is-error': isErrorState,
        'sparky-is-disabled': isDisabled,
        'sparky-c-file-upload-field--box': variant === 'box',
    });
    const hitAreaClassName = classnames('sparky-c-file-upload-field__hit-area', {
        'sparky-c-file-upload-field__hit-area--drag-over': isDragging,
    });
    return (_jsxs("div", Object.assign({ className: componentClassName }, { children: [_jsx(Label, { className: "sparky-c-file-upload-field__label", htmlFor: idVar, text: label, bold: false, hideLabel: hideLabel, labelAfter: labelAfter, required: required, optionalLabel: optionalLabel, requiredLabel: requiredLabel }), _jsx("div", Object.assign({ className: "sparky-c-file-upload-field__body" }, { children: _jsxs("div", Object.assign({ className: hitAreaClassName, onDragOver: () => onDragOver(), onDragEnd: () => onDragEnd(), onDragLeave: () => onDragEnd(), onDrop: () => onDragEnd() }, { children: [_jsx(TextInput, Object.assign({ className: "sparky-c-file-upload-field__input", type: "file", id: idVar, name: name, float: false, value: "", placeholder: placeholder, onChange: (e) => onFileInputChange(e), inputMode: inputMode, disabled: Boolean(isDisabled), readOnly: readOnly, required: required, ariaDescribedBy: ariaDescribedByVar, "aria-invalid": !!isError, multiple: multiple, accept: acceptedFileTypes }, other)), _jsx("div", Object.assign({ className: "sparky-c-file-upload-field__instructions" }, { children: iconLabel ? (_jsx(Icon, { name: iconLabel, className: "sparky-c-file-upload-field__instructions__icon", size: "lg" })) : (_jsx(_Fragment, { children: dragAndDropText ? (dragAndDropText) : (_jsxs(_Fragment, { children: ["Drag and drop files here or", ' ', _jsx("span", Object.assign({ className: "sparky-c-text-link" }, { children: "click to upload" }))] })) })) }))] })) })), fieldNote && (_jsx(FieldNote, Object.assign({ className: "sparky-c-file-upload-field__note", id: ariaDescribedByVar }, { children: fieldNoteState }))), filesState && (_jsx(TextList, Object.assign({ className: "sparky-c-file-upload-field__filelist" }, { children: filesState.map((file) => (_jsxs(TextList.Item, Object.assign({ className: "sparky-c-file-upload-field__filelist-item" }, { children: [_jsx("span", Object.assign({ className: "sparky-c-file-upload-field__filelist-item-data sparky-c-file-upload-field__filelist-item-name" }, { children: file.fileObject.name })), _jsx("span", Object.assign({ className: "sparky-c-file-upload-field__filelist-item-data sparky-c-file-upload-field__filelist-item-size" }, { children: formatBytes(file.fileObject.size, 1) })), renderFileListIconOrRemoveButton(file)] }), `sparky-c-file-upload-field__filelist-item-key-${file.id}`))) })))] })));
};
//# sourceMappingURL=FileUploadField.js.map